// FAQ marker-overlay math — the brief's marker contract (TP_FAQ_BRIEF §5):
//   { steps: [{ n, x, y, w, h, kind: "click" | "type" | "look" }] }
// Coordinates are NORMALIZED 0–1 relative to the image, written by the
// pipeline from the real element bounding boxes. The renderer multiplies by
// the rendered image size — implemented as CSS percentages, which IS that
// multiplication and survives any display size / DPR. Nothing is ever burned
// into the screenshot.
//
// Derivations (contract carries only the frame box + kind):
// - the numbered circle sits at the CENTRE of its highlight frame,
// - kind "click" additionally renders the cursor glyph (12px diamond +
//   "click" chip) near the frame's lower-right action area and pulses the
//   frame (design F8-B: the click frame carries the tpPulse animation).
// The design HTML's hand-placed circle offsets (e.g. 22% vs centre 25%) are
// design-time nudges; the binding data contract has no circle coordinate, so
// the centre is the single derivable truth.

const clamp01 = (v) => Math.min(1, Math.max(0, v));
// 0–1 → percent, rounded to 3 decimals so float noise (0.46 + 0.2 =
// 0.659999…) never leaks into style attributes.
const pct = (v) => Math.round(clamp01(v) * 100000) / 1000;

// Percent helpers — return numbers (0–100); callers format `${v}%`.
export const frameRect = (m) => ({
  leftPct: pct(m.x),
  topPct: pct(m.y),
  widthPct: pct(m.w),
  heightPct: pct(m.h),
});

export const markerCircle = (m) => ({
  leftPct: pct(m.x + m.w / 2),
  topPct: pct(m.y + m.h / 2),
});

// Cursor anchor for kind "click": 60% along the frame's width, just below its
// bottom edge (the design's own geometry: desktop x+0.6w / ~y+h+0.01,
// mobile x+0.6w / y+h+0.02). Clamped so it can never leave the image.
//
// `flip` is the HORIZONTAL half of that clamp, and it cannot be expressed as a
// coordinate: the glyph is a diamond plus a "click" chip of a FIXED ~60px, so
// how far it reaches past its anchor depends on the rendered width, not on the
// normalized box. At 1440 the slot is 850px wide and 60px is 7% — harmless. At
// 360 the slot is ~284px and the same chip is 21%, so an anchor at 85% put it
// 11px outside the picture (measured; control pass F-1). Past this threshold
// the glyph is therefore laid out LEFTWARDS from the anchor — the diamond stays
// exactly on the click point either way, only the label changes side.
// 0.75 is the safe threshold: it leaves 25% of the narrowest slot (~71px) for a
// 60px chip.
const CURSOR_FLIP_AT = 0.75;

export const markerCursor = (m) => {
  const anchor = m.x + 0.6 * m.w;
  return {
    leftPct: pct(anchor),
    topPct: pct(Math.min(m.y + m.h + 0.015, 0.96)),
    flip: anchor > CURSOR_FLIP_AT,
  };
};

// Defensive parse of a marker JSON block. Returns [] for anything malformed —
// a broken coordinate file must degrade to "no overlay", never crash the page.
export const parseMarkerSteps = (markerJson) => {
  const steps = markerJson && Array.isArray(markerJson.steps) ? markerJson.steps : [];
  return steps.filter(
    (s) =>
      s &&
      Number.isFinite(s.n) &&
      [s.x, s.y, s.w, s.h].every((v) => Number.isFinite(v) && v >= 0 && v <= 1)
  );
};
